import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { fetchUserEstimates, Estimate } from '@/services/estimateService';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Eye, ShoppingBag } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS, it } from 'date-fns/locale';
import { useLanguage } from '@/context/LanguageContext';
import { useNavigate } from 'react-router-dom';

const EstimatesPage: React.FC = () => {
  const { user } = useAuth();
  const { language, t, tl } = useLanguage();
  const dateLocale = language === 'fr' ? fr : language === 'it' ? it : enUS;
  const navigate = useNavigate();
  const [viewingEstimate, setViewingEstimate] = useState<Estimate | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Fetch user's estimates
  const { data: estimates = [], isLoading, error } = useQuery({
    queryKey: ['user', 'estimates', user?.id],
    queryFn: () => user?.id ? fetchUserEstimates(user.id) : Promise.resolve([]),
    enabled: !!user?.id,
  });

  const getStatusBadge = (status: Estimate['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">{t('estimates.pending')}</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{t('estimates.approved')}</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{t('estimates.rejected')}</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{t('estimates.completed')}</Badge>;
      default:
        return <Badge variant="outline">{tl('Inconnu', 'Unknown', 'Sconosciuto')}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { 
        addSuffix: true,
        locale: dateLocale
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid date';
    }
  };

  const handleViewEstimate = (estimate: Estimate) => {
    setViewingEstimate(estimate);
    setIsViewDialogOpen(true);
  };

  const formatShippingAddress = (address: string | { street: string; city: string; state: string; zip: string; country: string; }): string => {
    if (typeof address === 'string') {
      return address;
    } else {
      return `${address.street || ''}\n${address.city || ''}, ${address.state || ''} ${address.zip || ''}\n${address.country || ''}`.trim();
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white text-black">
        <div className="container mx-auto py-12 px-4">
          <div className="text-center">
            <p className="mb-4">{t('estimates.pleaseSignIn')}</p>
            <Button onClick={() => navigate('/auth')}>{t('estimates.signIn')}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-white text-black">
      <Helmet>
        <title>{t('estimates.title')} | Meubles Karim</title>
        <meta name="description" content={tl('Voir et suivre vos devis de meubles', 'View and track your furniture estimates', 'Visualizza e segui i tuoi preventivi di mobili')} />
      </Helmet>
      
      <div className="container mx-auto py-12 px-4">
        <h1 className="font-sans text-3xl font-medium mb-8 text-center">{t('estimates.title')}</h1>
        
        <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>{t('estimates.estimateRequests')}</CardTitle>
            <CardDescription>
              {t('estimates.trackStatus')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" role="status">
                  <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">{tl('Chargement...', 'Loading...', 'Caricamento...')}</span>
                </div>
                <p className="mt-4 text-muted-foreground">{t('estimates.loading')}</p>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-500">
                <p>{t('estimates.error')}</p>
              </div>
            ) : estimates.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">{t('estimates.noEstimates')}</h3>
                <p className="text-muted-foreground mb-6">
                  {t('estimates.noEstimatesDescription')}
                </p>
                <Button variant="luxury" onClick={() => navigate('/contact')}>
                  {t('estimates.requestEstimate')}
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('estimates.date')}</TableHead>
                      <TableHead>{t('estimates.items')}</TableHead>
                      <TableHead>{t('estimates.status')}</TableHead>
                      <TableHead className="text-right">{t('estimates.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {estimates.map((estimate) => (
                      <TableRow key={estimate.id}>
                        <TableCell className="whitespace-nowrap">{formatDate(estimate.created_at)}</TableCell>
                        <TableCell>{estimate.items?.length || 0} {t('estimates.itemsCount')}</TableCell>
                        <TableCell>{getStatusBadge(estimate.status)}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewEstimate(estimate)}
                          >
                            <Eye size={16} className="mr-2" /> {t('estimates.view')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
      
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl bg-white text-black">
          <DialogHeader>
            <DialogTitle>{t('estimates.estimateDetails')}</DialogTitle>
          </DialogHeader>
          
          {viewingEstimate && (
            <div className="mt-4 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('estimates.estimateId')}</h3>
                    <p className="font-mono">{viewingEstimate.id}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('estimates.status')}</h3>
                    <div>{getStatusBadge(viewingEstimate.status)}</div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('estimates.created')}</h3>
                    <p>{formatDate(viewingEstimate.created_at)}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('estimates.contactEmail')}</h3>
                    <p>{viewingEstimate.contact_email}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('estimates.contactPhone')}</h3>
                    <p>{viewingEstimate.contact_phone}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('estimates.shippingAddress')}</h3>
                    <p className="whitespace-pre-line">
                      {formatShippingAddress(viewingEstimate.shipping_address)}
                    </p>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="font-medium mb-3">{t('estimates.requestedItems')}</h3>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('estimates.item')}</TableHead>
                        <TableHead className="text-right">{t('estimates.quantity')}</TableHead>
                        {viewingEstimate.status === 'approved' && (
                          <TableHead className="text-right">{t('estimates.price')}</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewingEstimate.items && viewingEstimate.items.length > 0 ? (
                        viewingEstimate.items.map((item, index) => (
                          <TableRow key={`${item.product_id}-${index}`}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            {viewingEstimate.status === 'approved' && (
                              <TableCell className="text-right">
                                {tl(
                                  `${(item.price || 0).toLocaleString('fr-FR')} DT`,
                                  `${(item.price || 0).toLocaleString('en-US')} TND`,
                                  `${(item.price || 0).toLocaleString('it-IT')} TND`
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={viewingEstimate.status === 'approved' ? 3 : 2} className="text-center py-4 text-muted-foreground">
                            {t('estimates.noItems')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                
                {viewingEstimate.status === 'approved' && (
                  <div className="mt-4 flex justify-end">
                    <div className="bg-muted p-4 rounded-md">
                      <div className="flex justify-between items-center font-medium">
                        <span className="mr-8">{t('estimates.totalAmount')}</span>
                        <span className="text-lg">
                          {tl(
                            `${(viewingEstimate.total_amount || 0).toLocaleString('fr-FR')} DT`,
                            `${(viewingEstimate.total_amount || 0).toLocaleString('en-US')} TND`,
                            `${(viewingEstimate.total_amount || 0).toLocaleString('it-IT')} TND`
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstimatesPage;
